/**
 * Pantalla Pública de Turnos
 *
 * Diseñada para mostrar en TVs de sala de espera.
 * - 3 temas disponibles: Claro, Oscuro, Verde
 * - Turno actual grande y visible
 * - Lista de próximos turnos
 * - Auto-refresh cada 5 segundos
 * - Sin autenticación requerida
 * - Preferencia de tema guardada por sucursal
 * - Diseño responsive para móvil, tablet y TV
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Maximize, Minimize, Volume2, VolumeX, Palette } from "lucide-react";
import { usePantallaPublica } from "@/hooks/use-turnos";
import { useNotificationSound } from "@/hooks/use-notification-sound";

// Definición de temas
type ThemeKey = "light" | "dark" | "green";

interface Theme {
  name: string;
  pageBg: string;
  headerBg: string;
  cardBg: string;
  footerBg: string;
  queueItemBg: string;
  queueItemFirstBg: string;
  attendedBg: string;
  headerText: string;
  titleText: string;
  subtitleText: string;
  numberText: string;
  queueNumberText: string;
  queueNumberFirstText: string;
  mutedText: string;
  buttonBg: string;
  buttonHover: string;
  borderAccent: string;
  queueItemFirstBorder: string;
  queueItemBorder: string;
}

const themes: Record<ThemeKey, Theme> = {
  light: {
    name: "Claro",
    pageBg: "bg-slate-300",
    headerBg: "bg-emerald-700",
    cardBg: "bg-slate-200",
    footerBg: "bg-slate-300",
    queueItemBg: "bg-slate-100",
    queueItemFirstBg: "bg-emerald-50",
    attendedBg: "bg-slate-100",
    headerText: "text-white",
    titleText: "text-slate-700",
    subtitleText: "text-slate-500",
    numberText: "text-emerald-700",
    queueNumberText: "text-slate-700",
    queueNumberFirstText: "text-emerald-700",
    mutedText: "text-slate-400",
    buttonBg: "bg-emerald-600",
    buttonHover: "hover:bg-emerald-500",
    borderAccent: "border-emerald-500",
    queueItemFirstBorder: "border-emerald-500",
    queueItemBorder: "border-slate-300",
  },
  dark: {
    name: "Oscuro",
    pageBg: "bg-slate-900",
    headerBg: "bg-slate-800",
    cardBg: "bg-slate-800",
    footerBg: "bg-slate-900",
    queueItemBg: "bg-slate-700",
    queueItemFirstBg: "bg-emerald-900/50",
    attendedBg: "bg-slate-700/50",
    headerText: "text-white",
    titleText: "text-white",
    subtitleText: "text-slate-400",
    numberText: "text-emerald-400",
    queueNumberText: "text-slate-200",
    queueNumberFirstText: "text-emerald-400",
    mutedText: "text-slate-500",
    buttonBg: "bg-slate-700",
    buttonHover: "hover:bg-slate-600",
    borderAccent: "border-emerald-500",
    queueItemFirstBorder: "border-emerald-500",
    queueItemBorder: "border-slate-600",
  },
  green: {
    name: "Verde",
    pageBg: "bg-emerald-900",
    headerBg: "bg-emerald-950",
    cardBg: "bg-emerald-800",
    footerBg: "bg-emerald-900",
    queueItemBg: "bg-emerald-700",
    queueItemFirstBg: "bg-emerald-600",
    attendedBg: "bg-emerald-700/50",
    headerText: "text-white",
    titleText: "text-white",
    subtitleText: "text-emerald-300",
    numberText: "text-white",
    queueNumberText: "text-emerald-100",
    queueNumberFirstText: "text-white",
    mutedText: "text-emerald-400",
    buttonBg: "bg-emerald-700",
    buttonHover: "hover:bg-emerald-600",
    borderAccent: "border-emerald-400",
    queueItemFirstBorder: "border-yellow-400",
    queueItemBorder: "border-emerald-600",
  },
};

const themeOrder: ThemeKey[] = ["light", "dark", "green"];
const REFRESH_INTERVAL = 5000;

export default function PantallaPublicaTurnos() {
  const { sucursalCodigo } = useParams<{ sucursalCodigo: string }>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [currentTheme, setCurrentTheme] = useState<ThemeKey>("light");
  const lastTurnoIdRef = useRef<number | null>(null);
  const lastVecesLlamadoRef = useRef<number>(0);
  const { play: playNotification } = useNotificationSound({ volume: 0.7, type: "bell" });

  const { data, isLoading, isError } = usePantallaPublica(sucursalCodigo ?? "", {
    refetchInterval: REFRESH_INTERVAL,
    enabled: !!sucursalCodigo,
  });

  // Sound notification when turn changes or is recalled
  useEffect(() => {
    if (!data) return;
    const newTurnoId = data.turno_actual?.id ?? null;
    const newVecesLlamado = data.turno_actual?.veces_llamado ?? 0;
    const turnoChanged = newTurnoId && newTurnoId !== lastTurnoIdRef.current;
    const turnoRecalled =
      newTurnoId &&
      newTurnoId === lastTurnoIdRef.current &&
      newVecesLlamado > lastVecesLlamadoRef.current;
    if ((turnoChanged || turnoRecalled) && soundEnabled) {
      playNotification();
    }
    lastTurnoIdRef.current = newTurnoId;
    lastVecesLlamadoRef.current = newVecesLlamado;
  }, [data, soundEnabled, playNotification]);

  useEffect(() => {
    if (sucursalCodigo) {
      const savedTheme = localStorage.getItem(`pantalla-tema-${sucursalCodigo}`);
      if (savedTheme && themeOrder.includes(savedTheme as ThemeKey)) {
        setCurrentTheme(savedTheme as ThemeKey);
      }
    }
  }, [sucursalCodigo]);

  const cycleTheme = useCallback(() => {
    const currentIndex = themeOrder.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    const nextTheme = themeOrder[nextIndex];
    setCurrentTheme(nextTheme);
    if (sucursalCodigo) {
      localStorage.setItem(`pantalla-tema-${sucursalCodigo}`, nextTheme);
    }
  }, [currentTheme, sucursalCodigo]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const theme = themes[currentTheme];

  if (!sucursalCodigo) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center p-4`}>
        <p className={`${theme.subtitleText} text-lg sm:text-2xl text-center`}>
          Sucursal no especificada
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center p-4`}>
        <p className={`${theme.subtitleText} text-lg sm:text-2xl animate-pulse`}>Cargando...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center p-4`}>
        <p className="text-red-400 text-lg sm:text-2xl text-center">Error al cargar datos</p>
      </div>
    );
  }

  return (
    <div className={`h-screen ${theme.pageBg} flex flex-col overflow-hidden`}>
      {/* Header */}
      <header
        className={`${theme.headerBg} ${theme.headerText} px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 shadow-lg`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 md:gap-5 min-w-0">
            <img
              src="/logo-lab.png"
              alt="Logo"
              className="h-10 sm:h-14 md:h-20 w-auto flex-shrink-0"
            />
            <h1 className="text-lg sm:text-2xl md:text-4xl font-bold truncate">
              {data?.sucursal.nombre || "Laboratorio"}
            </h1>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={cycleTheme}
              className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors`}
              title={`Tema: ${theme.name}`}
            >
              <Palette className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors`}
              title={soundEnabled ? "Silenciar" : "Activar sonido"}
            >
              {soundEnabled ? (
                <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
            <button
              onClick={toggleFullscreen}
              className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors hidden sm:block`}
              title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Maximize className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6 min-h-0">
        {/* Current Turn */}
        <div
          className={`md:flex-[2] flex flex-col items-center justify-center ${theme.cardBg} rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 min-h-[200px] sm:min-h-[280px] md:min-h-0 overflow-hidden`}
        >
          <div className="text-center">
            <p
              className={`text-sm sm:text-xl md:text-2xl mb-1 sm:mb-2 ${theme.subtitleText} font-medium tracking-wide`}
            >
              TURNO ACTUAL
            </p>
            {data?.turno_actual ? (
              <>
                <div
                  className={`text-7xl sm:text-9xl md:text-[clamp(8rem,25vh,14rem)] font-bold leading-none ${theme.numberText} drop-shadow-sm`}
                >
                  {String(data.turno_actual.numero).padStart(3, "0")}
                </div>
                {data.turno_actual.punto_atencion && (
                  <div
                    className={`text-lg sm:text-2xl md:text-4xl ${theme.titleText} font-semibold mt-1 sm:mt-2 mb-2 sm:mb-4`}
                  >
                    {data.turno_actual.punto_atencion.nombre}
                  </div>
                )}
                {data.turno_actual.prioridad === "ALTA" && (
                  <div className="inline-block bg-red-500 text-white px-3 sm:px-6 py-1 sm:py-2 rounded-full text-sm sm:text-xl font-semibold">
                    PRIORIDAD ALTA
                  </div>
                )}
                {data.turno_actual.veces_llamado > 1 && (
                  <div className="mt-2 sm:mt-4 text-sm sm:text-xl text-amber-500 font-semibold animate-pulse">
                    ⚠️ Llamada #{data.turno_actual.veces_llamado}
                  </div>
                )}
              </>
            ) : (
              <div className={`text-5xl sm:text-7xl md:text-8xl ${theme.mutedText} font-light`}>
                ---
              </div>
            )}
          </div>
        </div>

        {/* Queue */}
        <div
          className={`md:flex-1 ${theme.cardBg} rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-4 md:p-6 flex flex-col min-h-[200px] md:min-h-0 md:overflow-hidden`}
        >
          <h2
            className={`text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-4 text-center ${theme.titleText} border-b-2 ${theme.borderAccent} pb-2 sm:pb-3`}
          >
            PRÓXIMOS TURNOS
          </h2>

          <div className="flex-1">
            {data?.cola && data.cola.length > 0 ? (
              <ul className="space-y-2 sm:space-y-3">
                {data.cola.slice(0, 5).map((turno, index) => (
                  <li
                    key={turno.id}
                    className={`flex items-center justify-between p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl transition-all ${
                      index === 0
                        ? `${theme.queueItemFirstBg} border-2 ${theme.queueItemFirstBorder} shadow-md`
                        : `${theme.queueItemBg} border ${theme.queueItemBorder}`
                    }`}
                  >
                    <span
                      className={`text-xl sm:text-2xl md:text-3xl font-bold ${
                        index === 0 ? theme.queueNumberFirstText : theme.queueNumberText
                      }`}
                    >
                      {String(turno.numero).padStart(3, "0")}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {turno.prioridad === "ALTA" && (
                        <span className="bg-red-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold">
                          ALTA
                        </span>
                      )}
                      {turno.prioridad === "MEDIA" && (
                        <span className="bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold">
                          MEDIA
                        </span>
                      )}
                      {turno.hora_llegada && (
                        <span className={`${theme.mutedText} text-xs sm:text-sm hidden sm:inline`}>
                          {turno.hora_llegada}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`text-center ${theme.mutedText} text-sm sm:text-lg mt-4 sm:mt-8`}>
                No hay turnos en espera
              </p>
            )}
          </div>

          {/* Attended Turns - Hidden on mobile */}
          {data?.atendidos && data.atendidos.length > 0 && (
            <div
              className={`mt-3 sm:mt-4 pt-3 sm:pt-4 border-t ${theme.queueItemBorder} hidden sm:block`}
            >
              <h3
                className={`text-xs sm:text-sm font-semibold mb-2 sm:mb-3 text-center ${theme.mutedText} uppercase tracking-wide`}
              >
                Atendidos
              </h3>
              <ul className="space-y-1 sm:space-y-2">
                {data.atendidos.slice(-5).map((turno) => (
                  <li
                    key={turno.id}
                    className={`flex items-center justify-between p-1.5 sm:p-2 rounded-lg ${theme.attendedBg} opacity-70`}
                  >
                    <span className={`text-base sm:text-xl font-bold ${theme.mutedText}`}>
                      {String(turno.numero).padStart(3, "0")}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-emerald-500 text-base sm:text-lg">✓</span>
                      {turno.hora_fin && (
                        <span className={`${theme.mutedText} text-xs sm:text-sm`}>
                          {turno.hora_fin}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer
        className={`px-4 sm:px-8 py-2 sm:py-3 text-center ${theme.mutedText} text-xs sm:text-sm ${theme.footerBg}`}
      >
        <span className="hidden sm:inline">Última actualización: </span>
        {data?.timestamp ? new Date(data.timestamp).toLocaleTimeString("es-HN") : "--:--:--"}
        <span className="mx-2 sm:mx-3">•</span>
        {theme.name}
      </footer>
    </div>
  );
}
